import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND, PILLARS } from "@/data/content";

const serviceLinks = PILLARS.map((p) => ({ label: p.title, to: p.to }));

const navLinks = [
  { label: "About", to: "/about" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Blog", to: "/blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-[#0A2540]/70 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Link to="/" data-testid="navbar-logo" className="group flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white p-0.5 ring-1 ring-gold/40">
              <img src={BRAND.logo} alt={`${BRAND.company} logo`} className="h-full w-full object-contain" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif text-lg font-semibold tracking-tight text-white">
                {BRAND.company}
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                {BRAND.name}
              </span>
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-gold transition-transform duration-300 group-hover:scale-150" />
          </Link>

          <nav className="hidden items-center gap-8 lg:flex">
            <NavLink
              to="/"
              data-testid="nav-home-link"
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors hover:text-gold ${isActive ? "text-gold" : "text-slate-300"}`
              }
            >
              Home
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="nav-services-dropdown"
                className="flex items-center gap-1 text-sm tracking-wide text-slate-300 outline-none transition-colors hover:text-gold data-[state=open]:text-gold"
              >
                Services <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="center"
                className="w-72 border-[#1E3A5F] bg-[#0A2540]/95 p-2 backdrop-blur-xl"
              >
                {serviceLinks.map((s) => (
                  <DropdownMenuItem key={s.to} asChild className="cursor-pointer rounded-lg px-4 py-3 focus:bg-white/5">
                    <Link to={s.to} data-testid={`nav-service-${s.to.slice(1)}`} className="text-sm text-slate-200">
                      {s.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors hover:text-gold ${isActive ? "text-gold" : "text-slate-300"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-cta-button" className="btn-primary !px-6 !py-2.5 !text-xs">
              Contact
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#051320]/98 px-8 backdrop-blur-xl lg:hidden"
          >
            <nav className="flex flex-col gap-2">
              {[{ label: "Home", to: "/" }, ...serviceLinks, ...navLinks, { label: "Contact", to: "/contact" }].map(
                (l, i) => (
                  <motion.div
                    key={l.to + l.label}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="group flex items-center justify-between border-b border-[#1E3A5F] py-4 font-serif text-2xl text-slate-200 transition-colors hover:text-gold"
                    >
                      {l.label}
                      <ArrowRight className="h-5 w-5 text-gold opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={1.5} />
                    </Link>
                  </motion.div>
                )
              )}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10"
            >
              <Link to="/contact" data-testid="mobile-nav-cta" className="btn-primary w-full">
                Book a Free Consultation
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
